import * as _ from "lodash";

import {
  IController,
  ILogService, IOnChangesObject,
  IPromise,
  IQService,
  IRequestConfig,
  IScope,
  ITimeoutService
} from "angular";
import {SplitService} from "../commons/services/split-service.service";
import {LocalStoreService} from "./local-store-service.service";
import {SyncService} from "./sync-service.service";
import {DataStoreConfig} from "./models/datastore-config.model";
import {PushNotificationsConfig} from "./models/push-notifications-config.model";
import {BringozApi, BringozApiAction} from "./models/bringoz-api.model";

export class BringozObject<T> {

  _dataStore: DataStoreConfig;
  _PN: PushNotificationsConfig;
  _id: string;

  private _api: BringozApi;
  private _siteId: string; // TODO: Remove
  private _cursor: string;
  private _scope: any;
  private _key: string;

  constructor(private $q: IQService,
              private $log: ILogService,
              private $timeout: ITimeoutService,
              private syncService: SyncService,
              private localStoreService: LocalStoreService,
              private splitService: SplitService,
              id: string,
              api: BringozApi,
              dataStore: DataStoreConfig,
              pushNotificationsConfig: PushNotificationsConfig,
              private currentApiCalls: any,
              private $$onUpdateTriggers: any) {

    this._dataStore = dataStore;
    this._PN = pushNotificationsConfig;
    this._id = encodeURIComponent(id);
    this._api = api;
    this._siteId = this.splitService.getSiteId(); // TODO: Remove
    this._cursor = null;
    this._scope = {};
    this._key = null;
  }

  $lock() {
    this.localStoreService.save<T>(this._dataStore, [_.assign({}, this.$value(), {$lock: true})]);
  }

  $unlock() {
    this.localStoreService.save<T>(this._dataStore, [_.assign({}, this.$value(), {$lock: false})]);
  }

  $externalUpdate(value: T) {
    let self = this;

    if (_.isUndefined(value)) {
      this.$log.error('[BringozObject] Value resolved to undefined!. --> @$externalUpdate', self);
    } else {

      _.set(self, '_scope.' + self._key, value);
      this.triggerHandlers(self, value);
    }
  }

  $asyncValue(params?: { [key: string]: any }, options?: { [key: string]: string }): IPromise<T> {
    let storeValue: T = this.$value();
    if (_.isUndefined(storeValue)) {
      return this.$get(params, options);
    } else {
      let deferred = this.$q.defer<T>();
      deferred.resolve(storeValue);
      return deferred.promise;
    }
  }

  $value(): T {
    return this.localStoreService.getValue<T>(this._dataStore, this._id);
  }

  $bind(scope: IScope, keyName: string): IPromise<T> {

    //FIXME
    if (_.isUndefined(scope) || _.isUndefined(keyName)) {
      this.$log.error('[BringozObject] Scope and/or keyName resolved to undefined!. --> @$bind');
      throw new Error('Fail to $bind, missing required parameters');
    } else {
      return this.handleBind(this, scope, keyName);
    }

  }

  $bindCtrl(scope: IScope, ctrl: IController, keyName: string, options?: { [key: string]: string }): IPromise<T> {

    if (_.isUndefined(scope) || _.isUndefined(ctrl) || _.isUndefined(keyName)) {
      this.$log.error('[BringozObject] Ctrl and/or keyName resolved to undefined!. --> @$bindCtrl');
      throw new Error('Fail to $bindCtrl, missing required parameters');
    } else {
      return this.handleBind(this, scope, keyName, ctrl, options);
    }
  }

  $bindCtrlToId(scope: IScope, ctrl: IController, keyName: string, idKey: string, options?: {
    [key: string]: string
  }): IPromise<T> {

    let self = this;
    let deferred = this.$q.defer();

    if (_.isUndefined(scope) || _.isUndefined(ctrl) || _.isUndefined(keyName) || _.isUndefined(idKey)) {
      this.$log.error('[BringozObject] Scope and/or key resolved to undefined!. --> @$bindCtrlToId');
      throw new Error('Fail to $bindCtrlToId, missing required parameters');
    } else {

      //FIXME - WTF - Used when SCROLLING with InfiniteScroll. its overriding the ctrl $OnChanges
      ctrl.$onChanges = (changes: IOnChangesObject) => {

        if (_.isFunction(ctrl.bindOnChanges)) {
          ctrl.bindOnChanges(changes);
        }

        if (changes[idKey]) {

          // if (!changes[idKey].isFirstChange() && !_.isEqual(changes[idKey].currentValue, changes[idKey].previousValue)) {
          if (!changes[idKey].isFirstChange() && !!changes[idKey].currentValue && !_.isEqual(changes[idKey].currentValue, changes[idKey].previousValue)) {

            if (_.get(self, '_scope', false) && _.get(self, '_key', false)) {

              let unsubscribeCb = self['_unsubscribeCb'];
              if (_.isUndefined(unsubscribeCb)) {
                this.$log.error('[BringozObject] Unsubscribe callback resolved to undefined!. --> @$bindCtrlToIdOptions');
              } else {
                unsubscribeCb();
              }
            }

            _.set(self, '_id', changes[idKey].currentValue);

            this.handleBind(self, scope, keyName, ctrl, options).then(deferred.resolve, deferred.reject);
          }

        }
      };

      return this.handleBind(self, scope, keyName, ctrl, options);
    }

  }

  $registerOnUpdateHandler(scope: IScope, handler: (entity: T) => any) {
    //FIXME
    let self = this;

    if (_.isFunction(handler)) {

      if (self._id && self._dataStore) {
        let container = _.get(this.$$onUpdateTriggers, self._dataStore.objectStoreName + '.' + self._id);
        if (container) {
          container.push(handler);
        } else {
          _.set(this.$$onUpdateTriggers, self._dataStore.objectStoreName + '.' + self._id, []);
          this.$$onUpdateTriggers[self._dataStore.objectStoreName][self._id].push(handler);
        }

        if (scope.$on) {
          scope.$on('$destroy', this.deregisterOnUpdateHandlerCallback(self, handler));
        } else {

          this.$log.error('[BringozObject] Scope is not IScope. --> @$registerOnUpdateHandler');

          //Scope is ctrl
          //FIXME
          // scope.$onDestroy = this.deregisterOnUpdateHandlerCallback(self, handler);
        }
        return this.deregisterOnUpdateHandlerCallback(self, handler);

      } else {
        this.$log.error('[BringozObject] The entity ID resolved to undefined!. --> @registerOnUpdateHandler');
      }

    } else {
      this.$log.error('[BringozObject] The handler must be a function which receives the entity itself. ' +
        '--> @registerOnUpdateHandler');
    }
  }


  $destroy(): void {
    let unsubscribeCb =  this['_unsubscribeCb'];
    if (_.isUndefined(unsubscribeCb)) {
      this.$log.error('[BringozObject] Unsubscribe callback resolved to undefined!. --> @$destroy');
    } else {
      unsubscribeCb();
    }
  }

  $update(data: T, options?: { [key: string]: string }, params?: { [key: string]: any }) {
    let self = this;
    let url: string = self._api.base + '/' + self._id;

    // Set site id query param to every call.
    if (!params) {
      params = {}
    }
    if (!params.siteId) {
      params.siteId = this.splitService.getSiteId()
    }

    return this.request(url, self._dataStore, {
      url: url,
      method: 'POST',
      params: params,
      data: data
    }, options, true);
  }

  $call(apiCall: BringozApiAction, params?: { [key: string]: any }, data?, options?, doUpdate?: boolean) {

    let self = this;

    let url: string = self._api.base + '/' + self._id + '/' + apiCall.address;
    let deferred = this.$q.defer();
    let updatable: boolean = apiCall.updateable || false;

    // Set site id query param to every call.
    if (!params) {
      params = {}
    }
    if (!params.siteId) {
      params.siteId = this.splitService.getSiteId()
    }

    this.request(url, self._dataStore, {
      url: url,
      method: apiCall.cmd,
      params: params,
      data: data,
      headers: apiCall.headers
    }, options, doUpdate || updatable).then(
      (response) => {

        deferred.resolve(response);
      },
      deferred.reject
    );

    return deferred.promise;
  }

  //TODO refactor
  /**** Request object by id, allows custom params (_params) and http request options (_options) ****/
  $get(params?: { [key: string]: any }, options?: { [key: string]: string }): IPromise<any> {

    let self = this;
    let deferred = this.$q.defer();  // Return deferred promise

    // Set site id query param to every call.
    if (!params) {
      params = {}
    }
    if (!params.siteId) {
      params.siteId = this.splitService.getSiteId()
    }

    // currentApiRef - Object contains all currently pending $get requests for this API end point
    let currentApiRef = _.get(this.currentApiCalls, self._api.base);
    if (!currentApiRef) {
      // If there are no $get calls pending, create an instance to be used by other $get calls
      _.set(this.currentApiCalls, self._api.base, {
        ids: [self._id],
        params: params,
        deferred: [{
          deferred: deferred,
          id: self._id
        }]
      });
      currentApiRef = _.get(this.currentApiCalls, self._api.base);
    } else {
      let indexFound = _.findIndex(currentApiRef.ids, (e) => {
        return e === self._id
      });
      if (indexFound === -1) {
        // This ID hasn't been requested yet (not pending a response)
        currentApiRef.ids.push(self._id);
      }

      // Add new params to earlier $get requests
      currentApiRef.params = _.assign(currentApiRef.params, params);
      // Save deferred promise to later invoke, when response received
      currentApiRef.deferred.push({
        deferred: deferred,
        id: self._id
      });
      // Restart sending timeout
      this.$timeout.cancel(currentApiRef.timerRef);
    }

    // Set timeout for delayed request.
    // The reason the request is delayed is to send as many requests together as possible.
    currentApiRef.timerRef = this.$timeout(() => {
      let queryParam = _.assign({}, currentApiRef.params);
      _.set(queryParam, 'ids', currentApiRef.ids); // Add ids as params in the request

      // Send out request
      this.request(self._api.base, self._dataStore, {
        url: self._api.base,
        method: 'GET',
        params: queryParam
      }, options, true)
        .then((res) => {
          if (_.get(res, 'list', false) || !!res.length) {
            // If response was a list, update all values and resolve all relevant promises
            let list = _.get(res, 'list', res);
            _.map(list, function (elem) {
              currentApiRef.deferred.forEach(function (deferred) {
                if (deferred.id === _.get(elem, self._dataStore.keyName)) {
                  deferred.deferred.resolve(elem);
                }

              });
            });
          } else {
            // Single object returned, resolve all promises
            currentApiRef.deferred.forEach((elem) => {
              elem.deferred.resolve(res);
            });
          }
          _.set(this.currentApiCalls, self._api.base, null);
        })
    }, 500);

    if (currentApiRef.ids.length >= 50) {
      _.set(this.currentApiCalls, self._api.base, null);
    }

    return deferred.promise;
  }

  $instantGet(_params?: { [key: string]: any }) {

    const url: string = this._api.base + '/' + this._id;

    if (!_params) {
      _params = {};
    }

    _params.siteId = this.splitService.getSiteId();

    return this.request(url, this._dataStore, {
      url: url,
      method: 'GET',
      params: _params,
    }, {}, true);
  }

  $post(data: T, _params?: { [key: string]: any }): IPromise<T> {
    let self = this;
    let url = self._api.base;

    if (self._id) {
      url = url + '/' + self._id;
    }

    if (!_params) {
      _params = {}
    }

    if (!_params.siteId) {
      _params.siteId = this.splitService.getSiteId()
    }

    let cleanData: T = this.clearTempData(data);

    return this.request(url, self._dataStore, {
      url: url,
      method: 'POST',
      params: _params,
      data: cleanData
    });
  }

  $put(_data: T): IPromise<any> {

    let self = this;
    let url = self._api.base;

    return this.request(url, self._dataStore, {
      url: url,
      method: 'PUT',
      data: _data,
      params: {
        siteId: this.splitService.getSiteId()
      }
    });
  }

  $delete(): IPromise<any> {

    let self = this;
    let url = self._api.base + '/' + self._id;

    return this.request(url, self._dataStore, {
      url: url,
      method: 'DELETE',
      params: {
        siteId: this.splitService.getSiteId()
      }
    });
  }

  private handleBind(self: BringozObject<T>, scope: IScope, key: string, ctrl?: IController,
                     options?: { [key: string]: string }, params?: { [key: string]: any }): IPromise<T> {

    let deferred = this.$q.defer<T>();

    // this._scope = scope;
    this._scope = ctrl || scope;
    this._key = key;

    this.$asyncValue(params, options).then((value) => {
        _.set(self, '_scope.' + key, value);
        // _.set(ctrl, key, value);
        this.triggerHandlers(self, value);
        deferred.resolve(value);
      },
      (error) => {
        this.$log.error('[BringozObject] Fail on $asyncValue. --> @handleBind', error);
        deferred.reject(error);
      }
    );

    let unsubscribeCb = this.syncService.subscribe<T>(self);

    _.set(self, '_unsubscribeCb', unsubscribeCb);

    if (scope.$on) {
      scope.$on('$destroy', unsubscribeCb);
    } else {
      this.$log.error('[BringozObject] Scope is not IScope. --> @handleBind');
      deferred.reject();
      //Scope is ctrl
      // scope.$onDestroy = unsubscribeCb;
    }
    return deferred.promise;
  }

  private clearTempData(data: T): any {
    return _.assign({}, data, {$lock: undefined});
  }

  private deregisterOnUpdateHandlerCallback(self, handler: (entity: T) => any) {
    //Remove the handler from the queue.
    return () => {
      let handlerContainer = _.get(this.$$onUpdateTriggers, self._dataStore.objectStoreName + '.' + self._id);
      let handlerIndex = handlerContainer.indexOf(handler);

      handlerContainer.splice(handlerIndex, 1); //Remove the handler from the queue.
    }
  }

  private triggerHandlers(self, value: T) {
    if (value) {
      let triggers = _.get(this.$$onUpdateTriggers, self._dataStore.objectStoreName + '.' + self._id);
      if (triggers) {
        _.forEach(triggers, (handler) => {
          handler(value);
        });
      }
    }
  }

  private request(api: string, dataStore: DataStoreConfig, config: IRequestConfig, options?: {
    [key: string]: string
  }, doUpdate?: boolean): IPromise<any> {

    const deferred = this.$q.defer();

    this.$log.debug(`[BringozObject] Request "${api}"`);

    this.syncService.request(api, config, dataStore, options, doUpdate)
      .then((response) => {
          deferred.resolve(response);
        },
        (error) => {
          this.$log.error(`[BringozObject] API - ${api}, Error during request :`, error);
          deferred.reject(error.data);
        }
      );

    return deferred.promise;
  }
}
